/**
 * 굿즈 존 자동 진열 (docs/build/16 §5 P3) — "굿즈를 만들었는데 월드 진열대에 안 나타난다" 해결.
 *
 * 문제: 굿즈 문서(goods_doc)에 상품을 넣어도, 월드 맵에 그 상품을 가리키는 store 오브젝트가
 *       없으면 진열대가 생기지 않는다. 운영자가 존 편집기에서 일일이 배치해야 했다.
 * 해결: 미배치 상품을 찾아, 걸어갈 수 있는 빈 칸에 store 오브젝트를 자동으로 놓는다.
 *
 * 순수 함수 — 게시·IO 는 엣지/관리자. 배치 규칙(빈 칸·이동성)은 기존 tiledEdit 헬퍼 재사용.
 */
import {
  addObject,
  getProp,
  isBlockedTile,
  listObjects,
  type TiledMap,
  type TiledObject,
} from "./tiledEdit";
import { goodsImageSlot, type GoodsProduct } from "./goods";

/** 맵에 이미 진열된(=store 오브젝트가 있는) goods_id 집합. */
export function placedGoodsIds(map: TiledMap): Set<string> {
  const out = new Set<string>();
  for (const o of listObjects(map)) {
    const id = getProp(o, "goods_id");
    if (o.type === "store" && typeof id === "string" && id) out.add(id);
  }
  return out;
}

export interface PlacementStatus {
  placed: string[]; // 문서에 있고 맵에도 진열된 상품
  unplaced: string[]; // 문서엔 있는데 맵에 진열대가 없는 상품(자동 진열 대상)
  orphan: string[]; // 맵엔 진열대가 있는데 문서에서 사라진 상품(정리 후보)
}

/** 문서 ↔ 맵 대조. 운영자에게 "무엇이 안 보이는지"를 알려주는 게 목적. */
export function goodsPlacementStatus(products: readonly GoodsProduct[], map: TiledMap): PlacementStatus {
  const inMap = placedGoodsIds(map);
  const inDoc = new Set(products.map((p) => p.id));
  return {
    placed: products.filter((p) => inMap.has(p.id)).map((p) => p.id),
    unplaced: products.filter((p) => !inMap.has(p.id)).map((p) => p.id),
    orphan: [...inMap].filter((id) => !inDoc.has(id)),
  };
}

/**
 * 진열대를 놓을 수 있는 빈 칸(그리드 좌표).
 * 조건: ①걸어갈 수 있음(충돌 아님) ②다른 오브젝트가 없음 ③스폰(중앙) 회피 — 진열대에 파묻히지 않게.
 * 정렬: 위→아래, 왼→오른(윗줄부터 채워 진열이 규칙적으로 보이게).
 */
export function freeStoreSlots(map: TiledMap): Array<{ gx: number; gy: number }> {
  const occupied = new Set<string>();
  for (const o of listObjects(map)) {
    occupied.add(`${Math.round(o.x / 48)},${Math.round(o.y / 48)}`);
  }
  const cx = Math.floor(map.width / 2);
  const cy = Math.floor(map.height / 2);

  const slots: Array<{ gx: number; gy: number }> = [];
  for (let gy = 0; gy < map.height; gy++) {
    for (let gx = 0; gx < map.width; gx++) {
      if (isBlockedTile(map, gx, gy)) continue;
      if (occupied.has(`${gx},${gy}`)) continue;
      if (gx === cx && gy === cy) continue; // 스폰 칸엔 놓지 않는다
      slots.push({ gx, gy });
    }
  }
  return slots;
}

/** store 오브젝트 1개 생성(기존 진열대와 같은 형태). goods_id 만으로 상세가 열리므로 나머지는 최소. */
function storeObject(product: GoodsProduct, gx: number, gy: number): TiledObject {
  return {
    id: `goods_${product.id}`,
    name: `goods_${product.id}`,
    type: "store",
    x: gx * 48,
    y: gy * 48,
    width: 48,
    height: 48,
    properties: [
      { name: "title_ko", type: "string", value: product.name_ko },
      { name: "goods_id", type: "string", value: product.id },
      // card_image: 대표 이미지 슬롯을 마커 썸네일로(진열대가 뭔지 멀리서 보이게)
      ...(product.images[0]
        ? [{ name: "card_image", type: "string" as const, value: goodsImageSlot(product.id, 1) }]
        : []),
    ],
  };
}

export interface PlacementResult {
  map: TiledMap;
  placed: string[]; // 이번에 새로 놓은 상품
  skipped: string[]; // 빈 칸이 부족해 못 놓은 상품
}

/**
 * 미배치 상품을 빈 칸에 진열. 빈 칸이 모자라면 놓을 수 있는 만큼만 놓고 나머지는 skipped 로 알린다
 * (조용히 누락하지 않는다 — 운영자가 존을 넓히거나 정리하도록).
 */
export function placeGoodsInMap(products: readonly GoodsProduct[], map: TiledMap): PlacementResult {
  const { unplaced } = goodsPlacementStatus(products, map);
  const byId = new Map(products.map((p) => [p.id, p]));
  const slots = freeStoreSlots(map);

  let result = map;
  const placed: string[] = [];
  const skipped: string[] = [];
  let si = 0;
  for (const id of unplaced) {
    const product = byId.get(id);
    if (!product) continue;
    const slot = slots[si];
    if (!slot) {
      skipped.push(id);
      continue;
    }
    result = addObject(result, storeObject(product, slot.gx, slot.gy));
    placed.push(id);
    si += 1;
  }
  return { map: result, placed, skipped };
}
